import React, { useState, useEffect, useCallback } from "react";
import { ActionIcon, Button, Popover, Stack, Box, Text, Group, ScrollArea, Checkbox, TextInput } from "@mantine/core";
import { MdPublic, MdClear, MdSearch } from "react-icons/md";
import { usePopoverManager } from "./PopoverManager";

interface CountryFilterControlProps {
    value: string[]; // Selected countries
    onChange: (countries: string[]) => void;
    availableCountries: string[]; // All available countries from data
    disabled?: boolean;
    loading?: boolean;
    maxVisibleItems?: number; // Maximum number of items to show before scrolling (default: 6)
}

export const CountryFilterControl: React.FC<CountryFilterControlProps> = ({
    value,
    onChange,
    availableCountries,
    disabled = false,
    loading = false,
    maxVisibleItems = 6
}) => {
    const { isPopoverOpen, togglePopover, setOpenPopover } = usePopoverManager();
    const [tempSelection, setTempSelection] = useState<string[]>(value);
    const [searchTerm, setSearchTerm] = useState("");

    const popoverId = "country-filter";
    const opened = isPopoverOpen(popoverId); // Update temp selection when value changes from parent
    useEffect(() => {
        setTempSelection(value);
    }, [value]);

    // Reset search term when popover opens
    useEffect(() => {
        if (opened) {
            setSearchTerm("");
        }
    }, [opened]);

    const handleToggle = () => {
        const wasOpened = opened;
        togglePopover(popoverId);

        if (!wasOpened) {
            // Reset temp selection to current value when opening
            setTempSelection(value);
            setSearchTerm("");
        }
    };

    const handleCountryToggle = (country: string) => {
        setTempSelection(prev => {
            if (prev.includes(country)) {
                return prev.filter(c => c !== country);
            } else {
                return [...prev, country];
            }
        });
    };

    const handleSelectAll = () => {
        setTempSelection([...availableCountries]);
    };

    const handleClearAll = () => {
        setTempSelection([]);
    };

    const handleApply = () => {
        onChange(tempSelection);
        setOpenPopover(null);
    };

    const handleCancel = () => {
        setTempSelection(value);
        setOpenPopover(null);
    };

    const handleClose = () => {
        // Only close if this popover is actually the one that's open
        if (opened) {
            handleCancel();
        }
    };

    // Check if there are pending changes
    const hasPendingChanges =
        tempSelection.length !== value.length ||
        !tempSelection.every(country => value.includes(country)) ||
        !value.every(country => tempSelection.includes(country));

    // Always show the filter as having no active filters when no countries are available
    // or when all available countries are selected (which means "show all")
    const hasFilters = availableCountries.length > 0 && value.length > 0 && value.length < availableCountries.length;

    const displayText =
        availableCountries.length === 0
            ? "All Countries" // No countries available, show all legacy + new data
            : value.length === 0 || value.length === availableCountries.length
              ? "All Countries"
              : `${value.length}`;

    // Filter countries based on search term
    const filteredCountries = availableCountries.filter(country => country.toLowerCase().includes(searchTerm.toLowerCase()));

    // Calculate dynamic height based on number of items to show
    const itemsToShow = filteredCountries.length === 0 ? 1 : Math.min(filteredCountries.length, maxVisibleItems);
    const itemHeight = 32; // Approximate height per checkbox item (including gap)
    const minHeight = 40; // Minimum height for empty state or single item
    const dynamicHeight = Math.max(minHeight, itemsToShow * itemHeight);

    return (
        <Popover
            opened={opened}
            withArrow={false}
            closeOnClickOutside={false}
            closeOnEscape={true}
            trapFocus={false}
            withinPortal={true}
            position="bottom-start"
            offset={4}
            onClose={handleClose}
            styles={{
                dropdown: {
                    padding: "12px",
                    minWidth: "320px",
                    maxWidth: "400px",
                    border: "2px solid #339af0",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(51, 154, 240, 0.15)",
                    backgroundColor: "#ffffff"
                }
            }}
        >
            <Popover.Target>
                <ActionIcon
                    variant={hasFilters ? "filled" : "light"}
                    size="sm"
                    onClick={handleToggle}
                    disabled={disabled || loading}
                    color={hasFilters ? "blue" : undefined}
                    title={displayText}
                >
                    <MdPublic size={16} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="sm">
                    {availableCountries.length > 0 ? (
                        <>
                            {/* Search Input */}
                            <TextInput
                                placeholder="Search countries..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                leftSection={<MdSearch size={16} />}
                                size="sm"
                                styles={{
                                    input: {
                                        border: "1px solid #e9ecef",
                                        backgroundColor: "#f8f9fa",
                                        borderRadius: "6px",
                                        "&:focus": {
                                            borderColor: "#339af0",
                                            backgroundColor: "#ffffff"
                                        }
                                    }
                                }}
                            />

                            {/* Select All / Clear All buttons */}
                            <Group gap="xs">
                                <Button size="xs" variant="subtle" onClick={handleSelectAll} disabled={tempSelection.length === availableCountries.length}>
                                    Select All
                                </Button>
                                <Button size="xs" variant="subtle" onClick={handleClearAll} disabled={tempSelection.length === 0}>
                                    Clear All
                                </Button>
                            </Group>

                            {/* Country List */}
                            <ScrollArea h={dynamicHeight} offsetScrollbars>
                                <Stack gap="xs">
                                    {filteredCountries.length > 0 ? (
                                        filteredCountries.map(country => (
                                            <Box key={country}>
                                                <Checkbox
                                                    label={country || "Unknown"}
                                                    checked={tempSelection.includes(country)}
                                                    onChange={() => handleCountryToggle(country)}
                                                    size="sm"
                                                    styles={{
                                                        label: {
                                                            fontSize: "14px",
                                                            cursor: "pointer"
                                                        },
                                                        input: {
                                                            cursor: "pointer"
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        ))
                                    ) : (
                                        <Text size="sm" c="dimmed" ta="center" py="md">
                                            No countries found matching "{searchTerm}"
                                        </Text>
                                    )}
                                </Stack>
                            </ScrollArea>

                            {/* Apply/Cancel buttons - always visible */}
                            <Group gap="xs" grow>
                                <Button size="xs" variant="filled" onClick={handleApply} color="blue" disabled={!hasPendingChanges}>
                                    Apply Filter
                                </Button>
                                <Button size="xs" variant="subtle" onClick={handleCancel}>
                                    Cancel
                                </Button>
                            </Group>
                        </>
                    ) : (
                        <Text size="sm" c="dimmed" ta="center" py="md">
                            No country data available.
                            <br />
                            Showing all records (including legacy data without country information).
                        </Text>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};
